<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ pageTitle }}</h1>
      </div>
      <div class="header-actions">
        <button 
                class="btn btn-primary" 
                @click="handleCreate"
              >
                <i class="ri-add-line"></i>
                新增服務條款
              </button>
      </div>
    </div>

    <div class="page-container">
    <div class="page-toolbar">
      <div class="search-box">
        <input
          v-model="searchQuery"
          type="text"
          class="form-control"
          placeholder="搜尋版本或標題..."
          @input="handleSearch"
        />
        <i class="ri-search-line"></i>
      </div>
      <div class="filter-group">
        <select
          v-model="selectedStatus"
          class="form-control"
          @change="handleStatusFilter"
        >
          <option value="">全部狀態</option>
          <option value="draft">草稿</option>
          <option value="published">已發布</option>
          <option value="archived">已歸檔</option>
        </select>
      </div>
      <div class="filter-group">
        <select
          v-model="selectedLanguage"
          class="form-control"
          @change="handleLanguageFilter"
        >
          <option value="">全部語言</option>
          <option value="zh-TW">繁體中文</option>
          <option value="zh-CN">簡體中文</option>
          <option value="en">English</option>
        </select>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>載入中...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p class="error-message">{{ error }}</p>
      <button class="btn btn-secondary" @click="fetchTerms">重試</button>
    </div>

    <div v-else class="table-wrapper">
      <table>
        <thead>
          <tr>
            <th>版本</th>
            <th>標題</th>
            <th>語言</th>
            <th>狀態</th>
            <th>生效日期</th>
            <th>發布時間</th>
            <th>建立時間</th>
            <th>操作</th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="terms.length === 0">
            <td colspan="8" class="empty-state">
              沒有找到服務條款
            </td>
          </tr>
          <tr v-for="term in terms" :key="term.id">
            <td>{{ term.version }}</td>
            <td>{{ term.title }}</td>
            <td>{{ term.language || 'zh-TW' }}</td>
            <td>
              <span :class="getStatusBadgeClass(term.status)">
                {{ getStatusText(term.status) }}
              </span>
            </td>
            <td>{{ formatDate(term.effectiveDate) }}</td>
            <td>{{ formatDate(term.publishedAt) }}</td>
            <td>{{ formatDate(term.createdAt) }}</td>
            <td>
              <div class="action-buttons">
                <button
                  class="btn btn-sm btn-secondary"
                  @click="handleEdit(term)"
                  title="編輯"
                >
                  <i class="ri-edit-line"></i>
                </button>
                <button
                  v-if="term.status !== 'published'"
                  class="btn btn-sm btn-success"
                  @click="handlePublish(term)"
                  title="發布"
                >
                  <i class="ri-send-plane-line"></i>
                </button>
                <button
                  class="btn btn-sm btn-danger"
                  @click="handleDelete(term)"
                  title="刪除"
                >
                  <i class="ri-delete-bin-line"></i>
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <ListPagination
      v-if="total > 0"
      :page="currentPage"
      :total-pages="totalPages"
      :total="total"
      @change="goToPage"
    />
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed } from 'vue';
import { useRouter } from 'vue-router';
import { usePageTitle } from '@/composables/usePageTitle.js';
import { termsService } from '@/api/modules/terms.js';
import { showSuccess, showError, showDeleteConfirm } from '@/utils/swal.js';

export default {
  name: 'TermsList',
  setup() {
    const router = useRouter();
    const { pageTitle } = usePageTitle('服務條款列表');
    const terms = ref([]);
    const loading = ref(false);
    const error = ref(null);
    const searchQuery = ref('');
    const selectedStatus = ref('');
    const selectedLanguage = ref('');
    const currentPage = ref(1);
    const pageSize = ref(20);
    const total = ref(0);
    const searchTimeout = ref(null);

    const totalPages = computed(() => {
      return Math.ceil(total.value / pageSize.value);
    });

    const fetchTerms = async () => {
      loading.value = true;
      error.value = null;

      try {
        const params = {
          page: Number(currentPage.value),
          pageSize: Number(pageSize.value),
        };

        if (searchQuery.value && searchQuery.value.trim().length > 0) {
          params.search = searchQuery.value.trim();
        }

        if (selectedStatus.value) {
          params.status = selectedStatus.value;
        }

        if (selectedLanguage.value) {
          params.language = selectedLanguage.value;
        }

        const response = await termsService.getTerms(params);

        if (response && response.success && response.data) {
          if (response.data.items) {
            terms.value = response.data.items || [];
            total.value = response.data.pagination?.totalItems || response.data.pagination?.total || 0;
          } else if (Array.isArray(response.data)) {
            terms.value = response.data;
            total.value = response.data.length;
          }
        } else {
          error.value = response && response.message || '獲取服務條款列表失敗';
        }
      } catch (err) {
        console.error('獲取服務條款列表失敗:', err);
        error.value = err.message || '獲取服務條款列表失敗';
      } finally {
        loading.value = false;
      }
    };

    const handleStatusFilter = () => {
      currentPage.value = 1;
      fetchTerms();
    };

    const handleLanguageFilter = () => {
      currentPage.value = 1;
      fetchTerms();
    };

    const handleSearch = () => {
      if (searchTimeout.value) {
        clearTimeout(searchTimeout.value);
      }
      searchTimeout.value = setTimeout(() => {
        currentPage.value = 1;
        fetchTerms();
      }, 500);
    };

    const goToPage = (page) => {
      if (page >= 1 && page <= totalPages.value) {
        currentPage.value = page;
        fetchTerms();
      }
    };

    const handleCreate = () => {
      router.push('/client/terms/create');
    };

    const handleEdit = (term) => {
      router.push(`/client/terms/${term.id}/edit`);
    };

    const handlePublish = async (term) => {
      try {
        const response = await termsService.publishTerm(term.id);
        if (response && response.success) {
          await showSuccess('發布成功', `服務條款「${term.title}」已成功發布`);
          await fetchTerms();
        } else {
          await showError('發布失敗', response?.message || '發布失敗');
        }
      } catch (err) {
        console.error('發布失敗:', err);
        await showError('發布失敗', err.message || '發布失敗，請稍後再試');
      }
    };

    const handleDelete = async (term) => {
      const confirmed = await showDeleteConfirm(`${term.title} (版本: ${term.version})`);
      
      if (!confirmed) {
        return;
      }

      const originalLoading = loading.value;
      loading.value = true;
      error.value = null;

      try {
        const response = await termsService.deleteTerm(term.id);

        if (response && response.success) {
          await showSuccess('刪除成功', `服務條款「${term.title}」已成功刪除`);
          
          if (terms.value.length === 1 && currentPage.value > 1) {
            currentPage.value = currentPage.value - 1;
          }
          
          await fetchTerms();
        } else {
          const errorMsg = response?.message || '刪除服務條款失敗';
          await showError('刪除失敗', errorMsg);
        }
      } catch (err) {
        console.error('刪除服務條款失敗:', err);
        await showError('刪除失敗', err.message || '刪除失敗，請稍後再試');
      } finally {
        loading.value = originalLoading;
      }
    };

    const formatDate = (dateString) => {
      if (!dateString) return '-';
      const date = new Date(dateString);
      return date.toLocaleDateString('zh-TW');
    };

    const getStatusBadgeClass = (status) => {
      const statusMap = {
        'draft': 'badge badge-secondary',
        'published': 'badge badge-success',
        'archived': 'badge badge-danger'
      };
      return statusMap[status] || 'badge badge-secondary';
    };

    const getStatusText = (status) => {
      const statusMap = {
        'draft': '草稿',
        'published': '已發布',
        'archived': '已歸檔'
      };
      return statusMap[status] || status;
    };

    onMounted(() => {
      fetchTerms();
    });

    return {
      terms,
      loading,
      error,
      searchQuery,
      selectedStatus,
      selectedLanguage,
      currentPage,
      totalPages,
      total,
      pageTitle,
      handleSearch,
      handleStatusFilter,
      handleLanguageFilter,
      goToPage,
      handleCreate,
      handleEdit,
      handlePublish,
      handleDelete,
      formatDate,
      getStatusBadgeClass,
      getStatusText,
      fetchTerms,
    };
  },
};
</script>

<style scoped>
@import '~@/assets/scss/components/_list-page-common.scss';

.table-wrapper table {
  min-width: 1000px;
}
</style>
